import { useCallback, useRef, useState } from 'react';
import type { MessageAttachment } from '@teo/widget-shared';
import { useWidget } from '../context/WidgetContext.js';
import { IconPause, IconPlay, IconStop } from '../components/icons.js';

type AudioPlayerProps = {
  attachment: MessageAttachment;
};

export function AudioPlayer({ attachment }: AudioPlayerProps) {
  const { t } = useWidget();
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [hasProgress, setHasProgress] = useState(false);

  const togglePlay = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (playing) {
      audio.pause();
      setPlaying(false);
      return;
    }

    try {
      await audio.play();
      setPlaying(true);
    } catch {
      setPlaying(false);
    }
  }, [playing]);

  const stop = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.pause();
    audio.currentTime = 0;
    setPlaying(false);
    setHasProgress(false);
  }, []);

  const label = attachment.fileName ?? 'Audio';

  return (
    <div className="teo-rich-media__audio">
      <audio
        ref={audioRef}
        src={attachment.url}
        preload="metadata"
        onEnded={() => {
          setPlaying(false);
          setHasProgress(false);
        }}
        onTimeUpdate={() => {
          const audio = audioRef.current;
          setHasProgress(Boolean(audio && audio.currentTime > 0));
        }}
        onPause={() => {
          if (audioRef.current?.currentTime === 0) setPlaying(false);
        }}
      />
      <button
        type="button"
        className="teo-rich-media__audio-btn"
        onClick={() => void togglePlay()}
        aria-label={playing ? t.mediaPause : t.mediaPlay}
      >
        {playing ? <IconPause /> : <IconPlay />}
      </button>
      <div className="teo-rich-media__audio-meta">
        <span className="teo-rich-media__audio-label">{label}</span>
      </div>
      <button
        type="button"
        className="teo-rich-media__audio-btn teo-rich-media__audio-btn--stop"
        onClick={stop}
        aria-label={t.mediaStop}
        disabled={!playing && !hasProgress}
      >
        <IconStop />
      </button>
    </div>
  );
}
